import { motion } from 'framer-motion';
import { CheckCircle2, Lightbulb, Target, Users2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui';
import { SectionHeading, FeatureCard } from '@/shared/components/SolarSections';
import { companyHighlights, founderProfile, cmdProfile } from '@/shared/utils/SolarData';

const aboutBullets = [
  'Committed to sustainable, innovative, and cost-effective solar energy solutions.',
  'Focused on helping customers reduce electricity costs and environmental impact.',
  'Driven by advanced solar technology and customer-focused service.'
];

const values = [
  {
    icon: Target,
    title: 'Mission',
    description:
      'Accelerate the transition to clean energy while helping customers save money and reduce their environmental footprint.'
  },
  {
    icon: Lightbulb,
    title: 'Vision',
    description:
      'Become a trusted leader in renewable energy solutions through dependable execution and premium service.'
  },
  {
    icon: Users2,
    title: 'Core Values',
    description:
      'Integrity, customer focus, technical excellence, accountability, and long-term partnership.'
  }
];

const AboutPage = () => {
  return (
    <div className="px-4 pt-8 pb-16 sm:px-6 lg:px-8 lg:pt-12 lg:pb-24">
      <div className="mx-auto max-w-7xl space-y-16">
        <SectionHeading
          eyebrow="About Us"
          title="Building a cleaner energy future with practical solar solutions"
          description="PS Group Solar Energy is committed to delivering sustainable, innovative, and cost-effective solar energy solutions for homes, businesses, and industries."
          align="center"
        />

        <div className="grid gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:items-start">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.25 }}
            className="space-y-6"
          >
            <Card className="border-border-primary/70 bg-background-card/90">
              <CardHeader>
                <CardTitle className="text-2xl">Company Introduction</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-sm leading-7 text-text-secondary">
                <p>
                  {founderProfile.biography}
                </p>
                <p>
                  Our mission is to accelerate the transition to clean energy while helping customers reduce electricity costs and environmental impact. Through advanced solar technology and customer-focused service, we aim to become a trusted leader in renewable energy solutions.
                </p>
                {aboutBullets.map(item => (
                  <div key={item} className="flex items-start gap-3">
                    <CheckCircle2 className="mt-1 h-5 w-5 text-accent-primary" />
                    <span>{item}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          </motion.div>

          <div className="grid gap-4 sm:grid-cols-3 lg:grid-cols-1">
            {values.map(value => (
              <FeatureCard key={value.title} {...value} />
            ))}
          </div>
        </div>

        <section className="space-y-8">
          <SectionHeading
            eyebrow="Why choose PS Group Solar Energy"
            title="A partner focused on quality, savings, and long-term support"
            description="We combine technical planning, premium equipment, and structured service to make solar ownership simple and dependable."
          />
          <div className="grid gap-6 md:grid-cols-3">
            {companyHighlights.map(item => (
              <FeatureCard key={item.title} {...item} />
            ))}
          </div>
        </section>

        <section className="space-y-8">
          <SectionHeading
            eyebrow="Leadership"
            title="The People Behind PS Group"
            description="Experienced leaders driving quality, growth, and customer trust across every project."
          />
          <div className="flex flex-col gap-6">
            {/* Director Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.25 }}
            >
              <Card className="overflow-hidden border-border-primary/70 bg-background-card/90">
                {/* Mobile: stacked with circular avatar */}
                <div className="flex flex-col sm:hidden">
                  <div className="flex items-center gap-4 p-5">
                    <img
                      src={founderProfile.image}
                      alt={founderProfile.name}
                      className="h-20 w-20 shrink-0 rounded-full object-cover object-top ring-2 ring-accent-primary/30"
                    />
                    <div>
                      <p className="text-lg font-bold text-text-primary">{founderProfile.name}</p>
                      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-accent-primary">{founderProfile.designation}</p>
                      <a href={`tel:${founderProfile.phone}`} className="mt-1 inline-flex items-center gap-1 text-sm text-text-secondary">
                        📞 {founderProfile.phone}
                      </a>
                    </div>
                  </div>
                  <div className="space-y-3 px-5 pb-5">
                    <p className="text-sm leading-6 text-text-secondary">{founderProfile.biography}</p>
                    <div className="rounded-2xl border border-accent-primary/20 bg-accent-primary/5 p-4">
                      <div className="flex gap-2">
                        <span className="text-2xl leading-none text-accent-primary">&ldquo;</span>
                        <p className="text-sm italic leading-6 text-text-primary">{founderProfile.leadershipMessage}</p>
                        <span className="self-end text-2xl leading-none text-accent-primary">&rdquo;</span>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Desktop: side image panel */}
                <div className="hidden sm:flex">
                  <div className="w-52 shrink-0">
                    <img
                      src={founderProfile.image}
                      alt={founderProfile.name}
                      className="h-full w-full object-cover object-top"
                    />
                  </div>
                  <div className="flex flex-1 flex-col justify-between gap-5 p-6">
                    <div className="space-y-1">
                      <p className="text-xl font-bold text-text-primary">{founderProfile.name}</p>
                      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-accent-primary">{founderProfile.designation}</p>
                      <p className="text-sm text-text-secondary">{founderProfile.biography}</p>
                    </div>
                    <div className="rounded-2xl border border-accent-primary/20 bg-accent-primary/5 p-4">
                      <div className="flex gap-2">
                        <span className="text-3xl leading-none text-accent-primary">&ldquo;</span>
                        <p className="text-sm italic leading-6 text-text-primary">{founderProfile.leadershipMessage}</p>
                        <span className="self-end text-3xl leading-none text-accent-primary">&rdquo;</span>
                      </div>
                    </div>
                    <div className="inline-flex w-fit items-center gap-2 rounded-full border border-border-primary/60 bg-background-secondary px-4 py-2 text-sm font-medium text-text-primary">
                      <span className="text-accent-primary">📞</span>
                      {founderProfile.phone}
                    </div>
                  </div>
                </div>
              </Card>
            </motion.div>

            {/* CMD Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.25 }}
              transition={{ delay: 0.1 }}
            >
              <Card className="overflow-hidden border-border-primary/70 bg-background-card/90">
                {/* Mobile: stacked with circular avatar */}
                <div className="flex flex-col sm:hidden">
                  <div className="flex items-center gap-4 p-5">
                    <img
                      src={cmdProfile.image}
                      alt={cmdProfile.name}
                      className="h-20 w-20 shrink-0 rounded-full object-cover object-top ring-2 ring-accent-primary/30"
                    />
                    <div>
                      <p className="text-lg font-bold text-text-primary">{cmdProfile.name}</p>
                      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-accent-primary">{cmdProfile.designation}</p>
                      <a href={`tel:${cmdProfile.phone}`} className="mt-1 inline-flex items-center gap-1 text-sm text-text-secondary">
                        📞 {cmdProfile.phone}
                      </a>
                    </div>
                  </div>
                  <div className="space-y-3 px-5 pb-5">
                    <p className="text-sm leading-6 text-text-secondary">{cmdProfile.summary}</p>
                    <div className="rounded-2xl border border-accent-primary/20 bg-accent-primary/5 p-4">
                      <div className="flex gap-2">
                        <span className="text-2xl leading-none text-accent-primary">&ldquo;</span>
                        <p className="text-sm italic leading-6 text-text-primary">
                          The leadership team works together to balance growth, customer trust, and disciplined project execution across every solar deployment.
                        </p>
                        <span className="self-end text-2xl leading-none text-accent-primary">&rdquo;</span>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Desktop: side image panel (reversed) */}
                <div className="hidden sm:flex sm:flex-row-reverse">
                  <div className="w-52 shrink-0">
                    <img
                      src={cmdProfile.image}
                      alt={cmdProfile.name}
                      className="h-full w-full object-cover object-top"
                    />
                  </div>
                  <div className="flex flex-1 flex-col justify-between gap-5 p-6">
                    <div className="space-y-1">
                      <p className="text-xl font-bold text-text-primary">{cmdProfile.name}</p>
                      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-accent-primary">{cmdProfile.designation}</p>
                      <p className="text-sm text-text-secondary">{cmdProfile.summary}</p>
                    </div>
                    <div className="rounded-2xl border border-accent-primary/20 bg-accent-primary/5 p-4">
                      <div className="flex gap-2">
                        <span className="text-3xl leading-none text-accent-primary">&ldquo;</span>
                        <p className="text-sm italic leading-6 text-text-primary">
                          The leadership team works together to balance growth, customer trust, and disciplined project execution across every solar deployment.
                        </p>
                        <span className="self-end text-3xl leading-none text-accent-primary">&rdquo;</span>
                      </div>
                    </div>
                    <div className="inline-flex w-fit items-center gap-2 rounded-full border border-border-primary/60 bg-background-secondary px-4 py-2 text-sm font-medium text-text-primary">
                      <span className="text-accent-primary">📞</span>
                      {cmdProfile.phone}
                    </div>
                  </div>
                </div>
              </Card>
            </motion.div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default AboutPage;